import styled from "styled-components";
import iamge1 from "../../../../../assets/img/contactless.jpg";
import iamge2 from "../../../../../assets/img/contactless1.jpg";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  WalletOutlined,
  BellOutlined,
  SafetyOutlined,
  FileProtectOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  LockOutlined,
} from "@ant-design/icons";

const featureCards = [
  {
    icon: <WalletOutlined />,
    number: "1 Wallet",
    label: "Unified wallet for everyday payments and government fees",
    delay: 100,
  },
  {
    icon: <BellOutlined />,
    number: "Real-time",
    label: "Transaction notifications delivered instantly to your device",
    delay: 200,
  },
  {
    icon: <SafetyOutlined />,
    number: "Trusted",
    label: "Official messages from verified institutions and government bodies",
    delay: 300,
  },
  {
    icon: <FileProtectOutlined />,
    number: "Permanent",
    label:
      "Digital records of all payments and communication, always accessible",
    delay: 400,
  },
];

const benefitCards = [
  {
    icon: <EnvironmentOutlined />,
    title: "Pay Anywhere, Anytime",
    body: "Access your wallet from any device and transact across multiple channels — mobile, web, or USSD.",
  },
  {
    icon: <ThunderboltOutlined />,
    title: "Instant Confirmations",
    body: "Every transaction is confirmed in real-time with a full receipt and audit trail you can access at any moment.",
  },
  {
    icon: <LockOutlined />,
    title: "Bank-Grade Security",
    body: "256-bit encryption and multi-factor authentication keep your finances and personal data completely protected.",
  },
];

export default function IndividualSolution() {
  return (
    <>
      {/* <PageHeader
        title="Individuals"
        description="Personal Finance & Communication in One Secure Platform"
        backgroundImage={iamge1}
      /> */}

      <Wrapper>
        <Inner className="container">
          {/* ── Section header ── */}
          <SectionHeader
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="700"
          >
            <Eyebrow>
              <EyebrowDot />
              For Individuals
            </Eyebrow>
            <SectionTitle>
              A Personal Digital Finance &amp; <GoldWord>Engagement</GoldWord>{" "}
              Hub
            </SectionTitle>
            <SectionSubtitle>
              OnePayX gives individuals clarity, confidence, and convenience —
              one secure platform for payments, records, and official
              communications.
            </SectionSubtitle>
          </SectionHeader>

          {/* ── Block 1: feature cards + image ── */}
          <Block>
            <CardsCol
              data-aos="fade-right"
              data-aos-delay="100"
              data-aos-duration="800"
            >
              <CardsGrid>
                {featureCards.map((item, i) => (
                  <FeatureCard
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={item.delay}
                    data-aos-duration="600"
                  >
                    <IconCircle>{item.icon}</IconCircle>
                    <StatNumber>{item.number}</StatNumber>
                    <StatLabel>{item.label}</StatLabel>
                  </FeatureCard>
                ))}
              </CardsGrid>
            </CardsCol>

            <ImageCol
              data-aos="fade-left"
              data-aos-delay="200"
              data-aos-duration="800"
            >
              <ImageFrame>
                <img src={iamge2} alt="Individual using OnePayX" />
                <ImageOverlay>
                  <OverlayBadge>
                    <OverlayBadgeDot />
                    Secure &amp; Instant
                  </OverlayBadge>
                </ImageOverlay>
              </ImageFrame>
            </ImageCol>
          </Block>

          <BlockDivider />

          {/* ── Block 2: image + benefit cards ── */}
          <Block $reverse>
            <BenefitCol
              data-aos="fade-left"
              data-aos-delay="150"
              data-aos-duration="800"
            >
              <BlockEyebrow>Why Choose OnePayX</BlockEyebrow>
              <BenefitCards>
                {benefitCards.map((item, i) => (
                  <BenefitCard
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={300 + i * 100}
                    data-aos-duration="600"
                  >
                    <BenefitIconWrap>{item.icon}</BenefitIconWrap>
                    <BenefitText>
                      <BenefitTitle>{item.title}</BenefitTitle>
                      <BenefitBody>{item.body}</BenefitBody>
                    </BenefitText>
                  </BenefitCard>
                ))}
              </BenefitCards>
            </BenefitCol>

            <ImageCol
              data-aos="fade-right"
              data-aos-delay="100"
              data-aos-duration="800"
            >
              <ImageFrame>
                <img src={iamge1} alt="Contactless payment" />
                <ImageOverlay>
                  <OverlayBadge>
                    <OverlayBadgeDot $gold />
                    Always Available
                  </OverlayBadge>
                </ImageOverlay>
              </ImageFrame>
            </ImageCol>
          </Block>
        </Inner>
      </Wrapper>
    </>
  );
}

/* ─── Styles ──────────────────────────────────────────────── */

const Wrapper = styled.section`
  background: #ffffff;
  padding-bottom: 80px;
`;

const Inner = styled.div`
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 52px;
  padding-top: 64px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${blueColor};
  background: ${blueColor}0f;
  border: 1px solid ${blueColor}28;
  border-radius: 20px;
  padding: 4px 12px;
  margin-bottom: 14px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -1px;
  color: #0f172a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const GoldWord = styled.span`
  color: ${goldColor};
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.7;
  margin: 0;
`;

const Block = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 52px;
  align-items: center;
  direction: ${(p) => (p.$reverse ? "rtl" : "ltr")};

  & > * {
    direction: ltr;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    direction: ltr;
    gap: 32px;
  }
`;

const BlockDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 52px 0;
`;

/* Feature cards */
const CardsCol = styled.div``;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 16px;
  padding: 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.25s ease;
  box-shadow: 0 2px 12px rgba(26, 111, 181, 0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 28px rgba(26, 111, 181, 0.13);
    border-color: ${blueColor}40;
  }
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${blueColor} 0%, #3b82f6 100%);
  border-radius: 12px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  box-shadow: 0 4px 12px ${blueColor}35;
`;

const StatNumber = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${blueColor};
  letter-spacing: -0.5px;
  margin-bottom: 6px;
`;

const StatLabel = styled.p`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
`;

/* Image */
const ImageCol = styled.div``;

const ImageFrame = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    display: block;
    object-fit: cover;
    border-radius: 18px;
    transition: transform 0.6s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 14px;
  left: 14px;
`;

const OverlayBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const OverlayBadgeDot = styled.span<{ $gold?: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$gold ? goldColor : "#22c55e")};
  box-shadow: 0 0 0 2px ${(p) => (p.$gold ? goldColor : "#22c55e")}40;
`;

/* Benefit cards */
const BenefitCol = styled.div``;

const BlockEyebrow = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 20px;
`;

const BenefitCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  background: #f8fafc;
  border: 1px solid #e8f0f8;
  border-left: 3px solid ${goldColor};
  border-radius: 12px;
  transition: all 0.25s ease;

  &:hover {
    background: #ffffff;
    box-shadow: 0 6px 20px rgba(26, 111, 181, 0.09);
    transform: translateX(4px);
  }
`;

const BenefitIconWrap = styled.div`
  width: 36px;
  height: 36px;
  background: ${blueColor}0f;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${blueColor};
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const BenefitText = styled.div``;

const BenefitTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
`;

const BenefitBody = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
`;
